import apiClient from '@/shared/api/client';
import type { ApiResponse } from '@/shared/api/types';

/**
 * The Skills Registry: agent-suite's on-disk modules (roles, personas, skill manifests, packs) and
 * how the runtime resolved them, decorated by the backend with each role's hire-ability and rollout
 * stage. Read-only — every edit to a skill goes through GitHub.
 */
export type RegistrySkillStatus = 'live' | 'dormant';
export type RegistryMethodFormat = 'SKILL.md' | 'method.md' | 'none';
export type RegistrySkillMode = 'plan' | 'propose' | 'access' | 'rank' | 'report';
export type RegistryRolloutStage = 'general' | 'internal_only';

export interface RegistryTrigger {
  type: 'check' | 'quota' | 'event';
  key: string;
  intervalDays?: number;
  minIntervalDays?: number;
  count?: number;
  period?: string;
  event?: string;
}

export interface RegistrySkill {
  key: string;
  dirName: string;
  owner: string;
  agentType: string;
  mode: RegistrySkillMode;
  objective: string;
  integration: string | null;
  auditType: string | null;
  produces: string | null;
  tools: string[];
  requires: string[];
  missingTools: string[];
  /** Registered but not wired: the skill runs, recording these as named gaps. */
  stubTools: string[];
  triggers: RegistryTrigger[];
  routes: string[];
  findingKinds: Record<string, readonly string[]>;
  hasEvidencePolicy: boolean;
  evidenceSubjects: string[];
  emitsCheckpoints: boolean;
  methodFormat: RegistryMethodFormat;
  referenceCount: number;
  reportableLabel: string | null;
  status: RegistrySkillStatus;
  dormantReasons: string[];
}

export interface RegistryPersona {
  name: string;
  role: string;
  mailbox: string;
  model: string;
  signature: string;
}

export interface RegistryRole {
  key: string;
  displayName: string;
  kpi: string;
  compliancePosture: 'non_phi' | 'phi';
  guardrails: string[];
  skills: string[];
  reportableSurfaces: Array<{ skillKey: string; label: string }>;
  persona: RegistryPersona | null;
  hasEvidencePolicy: boolean;
  counts: { skills: number; live: number; dormant: number };
  hireable: boolean;
  stage: RegistryRolloutStage;
}

export interface SkillsRegistry {
  roles: RegistryRole[];
  skills: RegistrySkill[];
  knowledgePacks: string[];
  counts: { skills: number; live: number; dormant: number };
}

export interface RegistrySkillDetail extends RegistrySkill {
  method: string;
  references: Record<string, string>;
  manifest: Record<string, unknown>;
}

// ─── Write-back (PR-per-change) ──────────────────────────────────────────────

export interface SkillFiles {
  owner: string;
  dirName: string;
  files: Record<string, string>;
}

export interface SkillValidationIssue {
  path: string;
  message: string;
}

export interface SkillValidationResult {
  ok: boolean;
  errors: SkillValidationIssue[];
  warnings: SkillValidationIssue[];
  summary: {
    key: string;
    mode: string;
    tools: string[];
    triggerCount: number;
    methodFormat: RegistryMethodFormat;
    referenceCount: number;
    dormant: boolean;
    replacesExisting: boolean;
  } | null;
}

export type SkillChangeKind = 'edit' | 'upload' | 'create';
export type SkillChangeStatus = 'pr_open' | 'merged' | 'closed';

export interface SkillChange {
  id: string;
  owner: string;
  dirName: string;
  skillKey: string | null;
  kind: SkillChangeKind;
  title: string;
  summary: string | null;
  branch: string;
  prNumber: number | null;
  prUrl: string | null;
  status: SkillChangeStatus;
  files: Array<{ path: string; action: 'write' | 'delete' }>;
  validation: SkillValidationResult;
  createdByUserId: string | null;
  createdAt: string;
  mergedAt: string | null;
  syncedAt: string | null;
}

export interface WriteBackStatus {
  configured: boolean;
  repo: string | null;
  baseBranch: string | null;
}

export interface ProposeSkillChangeInput {
  owner: string;
  dirName: string;
  kind: SkillChangeKind;
  title: string;
  summary?: string;
  files: Record<string, string>;
}

export const registryApi = {
  getRegistry: async (): Promise<SkillsRegistry> => {
    const { data } = await apiClient.get<ApiResponse<SkillsRegistry>>('/agency/registry');
    return data.data;
  },

  getSkill: async (key: string): Promise<RegistrySkillDetail> => {
    const { data } = await apiClient.get<ApiResponse<RegistrySkillDetail>>(
      `/agency/registry/skills/${encodeURIComponent(key)}`,
    );
    return data.data;
  },

  getWriteBack: async (): Promise<WriteBackStatus> => {
    const { data } = await apiClient.get<ApiResponse<WriteBackStatus>>('/agency/registry/write-back');
    return data.data;
  },

  getSkillFiles: async (key: string): Promise<SkillFiles> => {
    const { data } = await apiClient.get<ApiResponse<SkillFiles>>(
      `/agency/registry/skills/${encodeURIComponent(key)}/files`,
    );
    return data.data;
  },

  validate: async (input: Pick<ProposeSkillChangeInput, 'owner' | 'dirName' | 'files'>): Promise<SkillValidationResult> => {
    const { data } = await apiClient.post<ApiResponse<SkillValidationResult>>('/agency/registry/validate', input);
    return data.data;
  },

  propose: async (input: ProposeSkillChangeInput): Promise<SkillChange> => {
    const { data } = await apiClient.post<ApiResponse<SkillChange>>('/agency/registry/changes', input);
    return data.data;
  },

  listChanges: async (params: { owner?: string; dirName?: string } = {}): Promise<SkillChange[]> => {
    const { data } = await apiClient.get<ApiResponse<SkillChange[]>>('/agency/registry/changes', { params });
    return data.data;
  },
};
